import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiHeart, FiShoppingCart, FiTruck, FiRotateCcw, FiCheck, FiShield, FiTag, FiChevronDown, FiX } from 'react-icons/fi';
import { useShopping } from '../context/ShoppingContext';
import SEO from '../components/SEO';
import Reviews from '../components/Reviews';
import TrendingCard from '../components/TrendingCard';
import apiClient from '../utils/apiClient';
import { getImageUrl } from '../utils/imageUtils';
import '../styles/ProductDetail.css';
import '../styles/TrendingCard.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, addToWishlist, wishlistItems } = useShopping();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isAdded, setIsAdded] = useState(false);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [availablecoupons, setAvailablecoupons] = useState([]);
  const [bankOffers, setBankOffers] = useState([]);
  const [termsModal, setTermsModal] = useState({ show: false, title: '', content: '' });

  // Touch swipe state for mobile image gallery
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const mainImageRef = useRef(null);
  const minSwipeDistance = 50; // minimum distance to trigger swipe

  // Delivery options state
  const [deliveryPincode, setDeliveryPincode] = useState('201009'); // Default pincode
  const [deliveryOptions, setDeliveryOptions] = useState(null);
  const [loadingDelivery, setLoadingDelivery] = useState(false);

  // Accordion state for product info sections
  const [openSections, setOpenSections] = useState({
    itemDetails: false,
    measurements: false,
    features: false
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Fetch available coupons for this specific product
  useEffect(() => {
    const fetchcoupons = async () => {
      if (!product) return; // Wait for product to load

      try {
        // Send product info to filter coupons by applicable categories/products
        const productInfo = [{
          id: product.id,
          category: product.category || ''
        }];
        const response = await apiClient.post('/coupons/active', {
          orderTotal: 0,
          cartItems: productInfo
        });
        setAvailablecoupons(response.data || []);
      } catch (error) {
        console.error('Error fetching coupons:', error);
      }
    };
    fetchcoupons();
  }, [product]); // Re-fetch when product changes

  // Fetch active bank offers
  useEffect(() => {
    const fetchBankOffers = async () => {
      try {
        const response = await apiClient.get('/bank-offers/active');
        setBankOffers(response.data || []);
      } catch (error) {
        console.error('Error fetching bank offers:', error);
      }
    };
    fetchBankOffers();
  }, []);

  // Fetch delivery options when pincode changes
  useEffect(() => {
    const fetchDeliveryOptions = async () => {
      if (!deliveryPincode || deliveryPincode.length !== 6) {
        setDeliveryOptions(null);
        return;
      }

      setLoadingDelivery(true);
      try {
        const response = await apiClient.post('/delivery/options', {
          deliveryPincode: deliveryPincode,
          weight: 0.5
        });
        setDeliveryOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch delivery options:', error);
        // No fallback - show error
        setDeliveryOptions({
          available: false,
          error: true,
          message: 'Unable to check delivery. Please try again.'
        });
      } finally {
        setLoadingDelivery(false);
      }
    };

    fetchDeliveryOptions();
  }, [deliveryPincode]);

  // Fetch product data from API
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/products/${id}`);
        const productData = response.data;
        setProduct(productData);

        // Handle images array or single image fallback
        let imgs = productData.images || [];
        if (imgs.length === 0 && productData.image) {
          imgs = [productData.image];
        }
        setImages(imgs);

        // Fetch related products
        if (productData.category) {
          try {
            const relatedResponse = await apiClient.get(`/products?category=${productData.category}`);
            // Filter out the current product and limit to 4-5 items
            const related = relatedResponse.data.products
              .filter(p => p.id !== productData.id)
              .slice(0, 5);
            setRelatedProducts(related);
          } catch (relatedErr) {
            console.error('Error fetching related products:', relatedErr);
            // Don't fail the main page load if related products fail
          }
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  const isInWishlist = product ? wishlistItems.some((item) => item.id === product.id) : false;

  const handleAddToCart = () => {
    if (!product) return;
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 2000);
  };

  const handleWishlist = () => {
    if (!product) return;
    addToWishlist(product);
  };

  const handleQuantityChange = (value) => {
    if (value > 0) {
      setQuantity(value);
    }
  };

  const handleBuyNow = () => {
    if (!product) return;
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    navigate('/checkout');
  };

  // Touch swipe handlers for mobile image gallery
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const swipeDistance = touchStartX.current - touchEndX.current;

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        // Swipe left - go to next image
        setSelectedImage(prev => (prev < images.length - 1 ? prev + 1 : 0));
      } else {
        // Swipe right - go to previous image
        setSelectedImage(prev => (prev > 0 ? prev - 1 : images.length - 1));
      }
    }
  }, [images.length]);



  if (loading) {
    return (
      <div className="product-detail">
        <div className="product-detail-container">
          <div className="loading">Loading product details...</div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail">
        <div className="product-detail-container">
          <div className="error">
            {error || 'Product not found'}
            <button onClick={() => window.history.back()}>Go Back</button>
          </div>
        </div>
      </div>
    );
  }
  const visibleSectionsCount = product ? [
    product.specsVisibility?.itemDetails !== false,
    product.specsVisibility?.measurements !== false,
    product.specsVisibility?.features !== false
  ].filter(Boolean).length : 3;

  return (
    <div className="product-detail">
      <SEO
        title={product.name}
        description={product.description?.substring(0, 160) || `Shop ${product.name} - authentic handcrafted product from skilled artisans.`}
        canonical={`https://kalingastore.shop/product/${product.id}`}
        image={images[0] ? getImageUrl(images[0]) : undefined}
        type="product"
        product={{
          name: product.name,
          description: product.description,
          price: product.price,
          images: images.map(img => getImageUrl(img)),
          brand: product.brand,
          seller: product.seller,
          inStock: product.inStock !== false && product.stockQuantity > 0,
          rating: product.rating,
          reviews: product.reviews,
        }}
        breadcrumbs={[
          { name: 'Home', url: 'https://kalingastore.shop/' },
          { name: 'Products', url: 'https://kalingastore.shop/products' },
          { name: product.category || 'Crafts', url: `https://kalingastore.shop/shop/${product.category || 'handicrafts'}` },
          { name: product.name, url: `https://kalingastore.shop/product/${product.id}` },
        ]}
      />
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb">
        <a href="/">Home</a>
        <span className="breadcrumb-separator">›</span>
        <a href="/products">Products</a>
        <span className="breadcrumb-separator">›</span>
        <a href={`/products?category=${product.category || 'handicrafts'}`}>
          {product.category || 'Handicrafts'}
        </a>
        <span className="breadcrumb-separator">›</span>
        <span>{product.name}</span>
      </div>

      <div className="product-detail-container">
        {/* Left Column: Images */}
        <div className="product-images-section">
          <div className="thumbnail-list">
            {images.map((image, index) => (
              <img
                key={index}
                src={getImageUrl(image)}
                alt={`${product.name} ${index + 1}`}
                className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                onMouseEnter={() => setSelectedImage(index)}
                onClick={() => setSelectedImage(index)}
              />
            ))}
          </div>
          <div
            className="main-image-container"
            ref={mainImageRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={getImageUrl(images[selectedImage])}
              alt={product.name}
              className="main-image"
            />
            <div className="share-btn">
              <FiHeart className={isInWishlist ? 'wishlist-active' : ''} onClick={handleWishlist} />
            </div>
          </div>
        </div>

        {/* Right Column: Buy Box */}
        <div className="product-buy-box">
          <div className="buy-box-price">
            <sup>₹</sup>{product.price.toLocaleString('en-IN')}<sup>00</sup>
          </div>

          {/* Delivery Options from Shiprocket */}
          <div className="delivery-info-box">
            <div className="pincode-input-row">
              <FiTruck />
              <span>Deliver to</span>
              <input
                type="text"
                value={deliveryPincode}
                onChange={(e) => setDeliveryPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter pincode"
                maxLength={6}
                className="pincode-input"
              />
              {deliveryPincode.length === 6 && <FiCheck className="pincode-valid" />}
            </div>

            {loadingDelivery ? (
              <div className="delivery-loading-small">
                <div className="loading-spinner-small"></div>
                Checking delivery options...
              </div>
            ) : deliveryOptions?.available ? (
              <div className="delivery-estimates">
                {/* Quick Delivery Option - Same Day / Minutes (only show if charge < 200) */}
                {deliveryOptions.quick?.available && deliveryOptions.quick?.charge < 200 && (
                  <div className="delivery-option-card quick">
                    <div className="delivery-option-header">
                      <span className="delivery-type-badge quick">QUICK</span>
                      <span className="delivery-price">₹{deliveryOptions.quick?.charge}</span>
                    </div>
                    <div className="delivery-option-body">
                      <div className="delivery-date-line">
                        Get it in <strong>{deliveryOptions.quick?.estimatedTime}</strong>
                      </div>
                      <div className="courier-name">
                        On-demand delivery{deliveryOptions.quick?.distance ? ` (${deliveryOptions.quick.distance} km)` : ''}
                      </div>
                    </div>
                  </div>
                )}

                {/* Express Delivery Option - hide when Quick is showing */}
                {deliveryOptions.express?.available && !(deliveryOptions.quick?.available && deliveryOptions.quick?.charge < 200) && (
                  <div className="delivery-option-card express">
                    <div className="delivery-option-header">
                      <span className="delivery-type-badge express">EXPRESS</span>
                    </div>
                    <div className="delivery-option-body">
                      <div className="delivery-date-line">
                        Get it by <strong>{deliveryOptions.express?.estimatedDate}</strong>
                      </div>
                      {deliveryOptions.express?.courierName && (
                        <div className="courier-name">
                          via {deliveryOptions.express.courierName}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Standard Delivery Option */}
                {deliveryOptions.standard?.available && (
                  <div className="delivery-option-card standard">
                    <div className="delivery-option-header">
                      <span className="delivery-type-badge standard">STANDARD</span>
                    </div>
                    <div className="delivery-option-body">
                      <div className="delivery-date-line">
                        Get it by <strong>{deliveryOptions.standard?.estimatedDate}</strong>
                      </div>
                      {deliveryOptions.standard?.courierName && (
                        <div className="courier-name">
                          via {deliveryOptions.standard.courierName}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : deliveryOptions?.error ? (
              <div className="delivery-error-msg">
                <span className="error-icon">⚠️</span>
                <span>{deliveryOptions.message || 'Unable to check delivery. Please try again.'}</span>
                <button className="retry-btn" onClick={() => setDeliveryPincode(prev => prev)}>Retry</button>
              </div>
            ) : deliveryOptions && !deliveryOptions.available ? (
              <div className="delivery-not-available">
                <span className="not-available-icon">❌</span>
                <span>Sorry, we don't deliver to this pincode ({deliveryPincode})</span>
              </div>
            ) : deliveryPincode.length === 6 ? (
              <div className="delivery-loading-small">
                <div className="loading-spinner-small"></div>
                Checking...
              </div>
            ) : (
              <div className="delivery-unavailable-msg">
                <FiTruck className="hint-icon" />
                Enter pincode to check delivery options
              </div>
            )}
          </div>

          <div className="stock-status-large" style={{ color: (product.stockQuantity > 0 && product.inStock !== false) ? '#007600' : '#b12704' }}>
            {(product.stockQuantity > 0 && product.inStock !== false) ? 'In Stock' : 'Currently unavailable'}
          </div>

          <div className="seller-info">
            Delivered by <a href="#">{product.seller || 'Kalinga Crafts'}</a> and Sold by <a href="#">{product.seller || 'Kalinga Crafts'}</a>.
          </div>

          <div className="quantity-row">
            <label>Quantity:</label>
            <select value={quantity} onChange={(e) => handleQuantityChange(parseInt(e.target.value))}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>

          <button
            className="buy-box-btn add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={!product.inStock || (product.stockQuantity !== undefined && product.stockQuantity <= 0)}
          >
            {isAdded ? '✓ Added to Cart' : 'Add to Cart'}
          </button>

          <button
            className="buy-box-btn buy-now-btn"
            disabled={!product.inStock || (product.stockQuantity !== undefined && product.stockQuantity <= 0)}
            onClick={handleBuyNow}
          >
            Buy Now
          </button>

          <div className="secure-transaction">
            <FiShield /> Secure transaction
          </div>

          <div className="wishlist-link" onClick={handleWishlist}>
            {isInWishlist ? '✓ Added to Wish List' : 'Add to Wish List'}
          </div>
        </div>

        {/* Center Column: Product Info (Below grid on desktop) */}
        <div className="product-center-col">
          <h1 className="product-title">{product.name}</h1>

          <div className="product-store-link">
            Visit the {product.seller || 'Artisan'} Store
          </div>

          <div className="product-rating-row">
            <span className="stars">{'★'.repeat(Math.floor(product.rating || 4))}{'☆'.repeat(5 - Math.floor(product.rating || 4))}</span>
            <span className="rating-count">{product.rating || 3.8} ({product.reviews || 13} ratings)</span>
          </div>

          {product.reviews > 50 && (
            <div className="bought-count">
              {product.reviews > 100 ? '100+' : product.reviews} bought in past month
            </div>
          )}

          <div className="divider"></div>

          <div className="price-block">
            {/* Only show Limited Time Deal badge if enabled in admin */}
            {product.isLimitedTimeDeal && (
              <div className="limited-deal-badge">Limited time deal</div>
            )}
            <div className="price-row">
              <span className="currency-symbol">₹</span>
              <span className="price-whole">{Math.floor(product.price).toLocaleString('en-IN')}</span>
              <span className="price-fraction">00</span>
            </div>
            {product.originalPrice && (() => {
              const calculatedDiscount = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);
              return (
                <div className="mrp-row">
                  <span className="mrp-label">M.R.P.: </span>
                  <span className="mrp-value">₹{product.originalPrice.toLocaleString('en-IN')}</span>
                  <span className="discount-info">
                    ({calculatedDiscount > 0 ? `${calculatedDiscount}% off` : '0% off'})
                  </span>
                </div>
              );
            })()}
            <div className="tax-label">Inclusive of all taxes</div>
          </div>

          <div className="divider"></div>

          <div className="offers-block">
            <div className="offer-header">
              <span className="offer-icon"><FiTag /></span>
              <span className="offer-title">Offers & Coupons</span>
            </div>
            <div className="offer-items">
              {/* Dynamic Bank Offers */}
              {bankOffers.length > 0 ? (
                bankOffers.map((offer) => (
                  <div className="offer-card bank-offer-card-display" key={offer.id}>
                    <div className="bank-offer-badge">{offer.bankName}</div>
                    <h4>{offer.title}</h4>
                    <p>{offer.description}</p>
                    {offer.minOrderValue > 0 && (
                      <p className="offer-condition">On orders above ₹{offer.minOrderValue}</p>
                    )}
                    {offer.termsAndConditions && (
                      <a href="#" onClick={(e) => { e.preventDefault(); setTermsModal({ show: true, title: offer.title, content: offer.termsAndConditions }); }}>View Details &gt;</a>
                    )}
                  </div>
                ))
              ) : (
                <div className="offer-card bank-offer-card-display">
                  <div className="bank-offer-badge">Bank Offer</div>
                  <h4>Payment Offers</h4>
                  <p>Check back for exclusive bank discounts!</p>
                </div>
              )}
              {/* Dynamic Coupons */}
              {availablecoupons.length > 0 ? (
                availablecoupons.map((coupon) => (
                  <div className="offer-card coupon-card" key={coupon.code}>
                    <div className="coupon-badge">COUPON</div>
                    <h4><FiTag /> {coupon.code}</h4>
                    <p className="coupon-discount">
                      {coupon.discountType === 'percentage'
                        ? `${coupon.discountValue}% OFF`
                        : `₹${coupon.discountValue} OFF`}
                    </p>
                    <p className="coupon-desc">
                      {coupon.minOrderValue > 0
                        ? `On orders above ₹${coupon.minOrderValue}`
                        : 'No minimum order'}
                    </p>
                  </div>
                ))
              ) : (
                <div className="offer-card coupon-card">
                  <div className="coupon-badge">OFFERS</div>
                  <h4><FiTag /> Coming Soon</h4>
                  <p className="coupon-discount">Exciting Deals!</p>
                  <p className="coupon-desc">Check back for exclusive offers</p>
                </div>
              )}
            </div>
          </div>

          <div className="divider"></div>

          <div className="product-features">
            <div className="feature-icon-row">
              <div className="feature-item">
                <div className="icon-circle"><FiTruck /></div>
                <span>Free Delivery</span>
              </div>
              <div className="feature-item">
                <div className="icon-circle"><FiRotateCcw /></div>
                <span>30 Days Returns</span>
              </div>
              <div className="feature-item">
                <div className="icon-circle"><FiCheck /></div>
                <span>Top Brand</span>
              </div>
              <div className="feature-item">
                <div className="icon-circle"><FiShield /></div>
                <span>Secure Transaction</span>
              </div>
            </div>
          </div>

          <div className="divider"></div>

          <div className="about-item-section">
            <h3>About this item</h3>
            <ul className="about-bullets">
              <li>{product.description}</li>
              {product.details && product.details.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Product Information Sections */}
      <div className="product-info-sections">
        <h2 className="section-title">Product Information</h2>

        {/* Desktop Layout - Tabs with content grid below */}
        <div className="product-info-tabs desktop-only">
          {/* Tab Headers Row - Only show tabs for VISIBLE sections */}
          <div className="info-tabs-header">
            {(product.specsVisibility?.itemDetails !== false) && (
              <div
                className={`info-tab ${openSections.itemDetails ? 'active' : ''}`}
                onClick={() => toggleSection('itemDetails')}
              >
                <h3>{product.specsTitles?.itemDetails || 'Item Details'}</h3>
                <FiChevronDown className="accordion-icon" />
              </div>
            )}

            {(product.specsVisibility?.measurements !== false) && (
              <div
                className={`info-tab ${openSections.measurements ? 'active' : ''}`}
                onClick={() => toggleSection('measurements')}
              >
                <h3>{product.specsTitles?.measurements || 'Measurements'}</h3>
                <FiChevronDown className="accordion-icon" />
              </div>
            )}

            {(product.specsVisibility?.features !== false) && (
              <div
                className={`info-tab ${openSections.features ? 'active' : ''}`}
                onClick={() => toggleSection('features')}
              >
                <h3>{product.specsTitles?.features || 'Features & Specs'}</h3>
                <FiChevronDown className="accordion-icon" />
              </div>
            )}
          </div>

          {/* Content Grid - Visible columns shift left automatically in the grid */}
          <div
            className="info-content-grid"
            style={{
              gridTemplateColumns: `repeat(${visibleSectionsCount || 3}, 1fr)`
            }}
          >
            {/* Column: Item Details */}
            {(product.specsVisibility?.itemDetails !== false) && (
              <div className={`info-column ${openSections.itemDetails ? 'active' : 'empty'}`}>
                {openSections.itemDetails && (
                  <div className={`info-content ${typeof product.itemDetails === 'string' ? 'text-content' : ''}`}>
                    {Array.isArray(product.itemDetails) && product.itemDetails.length > 0 ? (
                      <table className="info-table">
                        <tbody>
                          {product.itemDetails.map((item, index) => (
                            (item.key || item.value) && (
                              <tr key={index}>
                                <td className="info-key">{item.key}</td>
                                <td className="info-value">{item.value}</td>
                              </tr>
                            )
                          ))}
                        </tbody>
                      </table>
                    ) : product.itemDetails ? (
                      <p>{product.itemDetails}</p>
                    ) : (
                      <p>{product.description}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Column: Measurements */}
            {(product.specsVisibility?.measurements !== false) && (
              <div className={`info-column ${openSections.measurements ? 'active' : 'empty'}`}>
                {openSections.measurements && (
                  <div className="info-content">
                    {product.measurements && product.measurements.length > 0 ? (
                      <table className="info-table">
                        <tbody>
                          {product.measurements.map((item, index) => (
                            <tr key={index}>
                              <td className="info-key">{item.key}</td>
                              <td className="info-value">{item.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="no-info">No measurements available</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Column: Features */}
            {(product.specsVisibility?.features !== false) && (
              <div className={`info-column ${openSections.features ? 'active' : 'empty'}`}>
                {openSections.features && (
                  <div className="info-content">
                    {product.customAttributes && product.customAttributes.length > 0 &&
                      product.customAttributes.some(item => item.key || item.value) ? (
                      <table className="info-table">
                        <tbody>
                          {product.customAttributes.map((item, index) => (
                            (item.key || item.value) && (
                              <tr key={index}>
                                <td className="info-key">{item.key}</td>
                                <td className="info-value">{item.value}</td>
                              </tr>
                            )
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="no-info">No specifications available</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Layout - Accordion with content directly below each header */}
        <div className="product-info-accordion mobile-only">
          {/* Item Details Accordion */}
          {(product.specsVisibility?.itemDetails !== false) && (
            <div className={`accordion-item ${openSections.itemDetails ? 'open' : ''}`}>
              <div className="accordion-header" onClick={() => toggleSection('itemDetails')}>
                <h3>Item Details</h3>
                <FiChevronDown className="accordion-icon" />
              </div>
              {openSections.itemDetails && (
                <div className="accordion-content">
                  <div className={`info-content ${typeof product.itemDetails === 'string' ? 'text-content' : ''}`}>
                    {Array.isArray(product.itemDetails) && product.itemDetails.length > 0 ? (
                      <table className="info-table">
                        <tbody>
                          {product.itemDetails.map((item, index) => (
                            (item.key || item.value) && (
                              <tr key={index}>
                                <td className="info-key">{item.key}</td>
                                <td className="info-value">{item.value}</td>
                              </tr>
                            )
                          ))}
                        </tbody>
                      </table>
                    ) : product.itemDetails ? (
                      <p>{product.itemDetails}</p>
                    ) : (
                      <p>{product.description}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Measurements Accordion */}
          {(product.specsVisibility?.measurements !== false) && (
            <div className={`accordion-item ${openSections.measurements ? 'open' : ''}`}>
              <div className="accordion-header" onClick={() => toggleSection('measurements')}>
                <h3>Measurements</h3>
                <FiChevronDown className="accordion-icon" />
              </div>
              {openSections.measurements && (
                <div className="accordion-content">
                  <div className="info-content">
                    {product.measurements && product.measurements.length > 0 ? (
                      <table className="info-table">
                        <tbody>
                          {product.measurements.map((item, index) => (
                            <tr key={index}>
                              <td className="info-key">{item.key}</td>
                              <td className="info-value">{item.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="no-info">No measurements available</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Features & Specs Accordion */}
          {(product.specsVisibility?.features !== false) && (
            <div className={`accordion-item ${openSections.features ? 'open' : ''}`}>
              <div className="accordion-header" onClick={() => toggleSection('features')}>
                <h3>Features & Specs</h3>
                <FiChevronDown className="accordion-icon" />
              </div>
              {openSections.features && (
                <div className="accordion-content">
                  <div className="info-content">
                    {product.customAttributes && product.customAttributes.length > 0 &&
                      product.customAttributes.some(item => item.key || item.value) ? (
                      <table className="info-table">
                        <tbody>
                          {product.customAttributes.map((item, index) => (
                            (item.key || item.value) && (
                              <tr key={index}>
                                <td className="info-key">{item.key}</td>
                                <td className="info-value">{item.value}</td>
                              </tr>
                            )
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="no-info">No specifications available</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Related Products Section */}
      {relatedProducts.length > 0 && (
        <div className="related-products">
          <h2>Products related to this item</h2>
          <p>Sponsored products related to this item</p>
          <div className="trending-grid">
            {relatedProducts.map(relatedProduct => (
              <TrendingCard key={relatedProduct.id} product={relatedProduct} />
            ))}
          </div>
        </div>
      )}

      {/* Reviews Section */}
      {product && <Reviews productId={product.id} />}

      {/* Terms & Conditions Modal */}
      {termsModal.show && (
        <div className="terms-modal-overlay" onClick={() => setTermsModal({ show: false, title: '', content: '' })}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h3>{termsModal.title}</h3>
              <button className="terms-modal-close" onClick={() => setTermsModal({ show: false, title: '', content: '' })}>
                <FiX />
              </button>
            </div>
            <div className="terms-modal-body">
              <h4>Terms & Conditions</h4>
              {termsModal.content.split('\n').map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
